import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { GeoJSONProps } from 'react-leaflet';
import { CityData } from '../components/CityOverviewCharts'


interface TokyoGeoJSONProps extends GeoJSONProps {
  data: any;
  cityData: CityData;
  lang: string;
  map: L.Map | null;
}

const TokyoGeoJSON: React.FC<TokyoGeoJSONProps> = ({ data, cityData, lang, map }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const layerRef = useRef<L.GeoJSON | null>(null);

  const updateLayer = useCallback(() => {
    if (!map || !data || !cityData) return;

    console.log('Updating layer for language:', lang); // Debug log

    if (layerRef.current) {
      map.removeLayer(layerRef.current);
    }

    const geoJsonLayer = L.geoJSON(data, {
      style: (feature) => ({
        fillColor: feature?.properties?.area_en === 'Tokubu' ? '#8fbc8f' : '#9acd32',
        weight: 2,
        opacity: 1,
        color: 'white',
        dashArray: '3',
        fillOpacity: 0.7,
      }),
      onEachFeature: (feature, layer) => {
        const key = feature?.properties?.key;
        const cityName = t(`cities.${key}`);

        if (feature.properties && cityData) {
          const cityInfo = cityData[key];
          console.log(`city info: ${cityInfo}`);
          if (!cityInfo) {
            console.log(`city info is undefined, skipping`);
            return
          }
          const yearlyData = Object.values(cityData[key]);
          const firstYear = yearlyData[0];
          const lastYear = yearlyData[yearlyData.length - 1];
          const firstMonth = Object.values(firstYear)[0];
          const lastMonth = Object.values(lastYear)[Object.values(lastYear).length - 1];
          const initialPopulation = firstMonth.population.value;
          const finalPopulation = lastMonth.population.value;
          const diff = finalPopulation-initialPopulation;
          const pop = t('site.map.population');
          if (cityInfo) {
            layer.bindTooltip(`<b>${cityName}</b><br>${pop}(${lastMonth.population.date.year}/${lastMonth.population.date.month}): ${finalPopulation} (${diff})`, {permanent: false, direction: 'auto'});
          } else {
            console.log(`cityinfo for ${cityName} (${key}) is blank`);
          }
        }

        layer.on({
          click: () => navigate(`/city/${key}`),
          mouseover: (e) => {
            const highlightStyle = {
              fillColor: feature?.properties?.area_en === 'Tokubu' ? '#90ee90' : '#98fb98',
              fillOpacity: 0.9,
            };
            e.target.setStyle(highlightStyle);
          },
          mouseout: (e) => {
            const resetStyle = {
              fillColor: feature?.properties?.area_en === 'Tokubu' ? '#8fbc8f' : '#9acd32',
              fillOpacity: 0.7,
            };
            e.target.setStyle(resetStyle);
          },
        });
      },
    });

    layerRef.current = geoJsonLayer;
    geoJsonLayer.addTo(map);
  }, [data, cityData, lang, map, t, navigate]);

  useEffect(() => {
    updateLayer();
  }, [data, cityData, lang, updateLayer]);

  return null;
};

export default TokyoGeoJSON;
